import React, { useEffect, useState } from 'react';

import { Table, Typography, Tag, Alert, Descriptions } from 'antd';
import { createCoreAxiosInstance } from 'createAxiosInstance';
import PropTypes from 'prop-types';

const { Title, Text } = Typography;

const columns = [
	{
		title: 'ID',
		dataIndex: 'id',
		key: 'id',
		width: 80,
	},
	{
		title: 'Type',
		dataIndex: 'type',
		key: 'type',
		width: 200,
		render: (type) => <Tag color="red">{type}</Tag>,
	},
	{
		title: 'Message',
		dataIndex: 'message',
		key: 'message',
		ellipsis: true,
		render: (message) => (
			<Text ellipsis={{ tooltip: false }} style={{ maxWidth: 400 }}>
				{message}
			</Text>
		),
	},
	{
		title: 'Created At',
		dataIndex: 'created_at',
		key: 'created_at',
		width: 200,
		render: (date) => (date ? new Date(date).toLocaleString() : '-'),
	},
];

const expandedRowRender = (record) => (
	<Descriptions bordered column={1} size="small">
		<Descriptions.Item label="Message">
			<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
				{record.message || '-'}
			</pre>
		</Descriptions.Item>
		<Descriptions.Item label="Items">
			<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
				{record.items ? JSON.stringify(record.items, null, 2) : '-'}
			</pre>
		</Descriptions.Item>
		<Descriptions.Item label="Modifiers">
			<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
				{record.modifiers ? JSON.stringify(record.modifiers, null, 2) : '-'}
			</pre>
		</Descriptions.Item>
	</Descriptions>
);

PosExceptions.propTypes = {
	orderId: PropTypes.number,
};

export default function PosExceptions({ orderId }) {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!orderId) return;

		let cancelled = false;
		const fetchExceptions = async () => {
			setLoading(true);
			setError(null);
			try {
				const axios = await createCoreAxiosInstance();
				const response = await axios.get(
					`/api/corpa/pos-exceptions/${orderId}/`,
				);
				if (!cancelled) {
					setData(response.data);
				}
			} catch (err) {
				if (!cancelled) {
					setError(err.message || 'Failed to load POS exceptions');
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};
		fetchExceptions();
		return () => {
			cancelled = true;
		};
	}, [orderId]);

	return (
		<>
			<Title level={4}>POS Exceptions</Title>
			{error && (
				<Alert
					message="Error loading POS exceptions"
					description={error}
					type="error"
					showIcon
					style={{ marginBottom: 16 }}
				/>
			)}
			<Table
				columns={columns}
				dataSource={data}
				rowKey="id"
				loading={loading}
				expandable={{ expandedRowRender }}
				pagination={false}
				locale={{ emptyText: 'No POS exceptions for this order' }}
			/>
		</>
	);
}
